

import {Component, Mixins, Watch} from "vue-property-decorator";
import SimpleInputMixin from "@/mixins/SimpleInputMixin.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import RaporForm from "@/components/forms/RaporForm.vue";
import {RaporEntity} from "@/entity/RaporEntity";
import {ProgramPaths} from "@/enum/ProgramPaths";
import SayfaBilgiInfo from "@/components/SayfaBilgiInfo.vue";
import RaporAiSihirbazi from "@/components/rapor/RaporAiSihirbazi.vue";
import RaporSayfaBasligi from "@/components/rapor/RaporSayfaBasligi.vue";
import RaporSekmeler from "@/components/rapor/RaporSekmeler.vue";
import RaporExcelCiktilari from "@/components/rapor/RaporExcelCiktilari.vue";

interface RaporSekme {
  kod: string;
  etiket: string;
  ikon: string;
  sayi?: number | null;
}

const SON_RAPOR_ANAHTARI = "rapor-son-goruntulenenler";
const SON_RAPOR_SINIRI = 6;

interface SonRapor {
  id: number;
  isim: string;
  zaman: number;
}

@Component({
  components: {RaporSekmeler, RaporSayfaBasligi, SayfaBilgiInfo, DeleteDialog, RaporForm, RaporAiSihirbazi, RaporExcelCiktilari}
})

export default class RaporList extends Mixins(SimpleInputMixin) {
  localValue!: Array<RaporEntity>;
  searchText: string = "";
  search: string = "";
  programPaths = ProgramPaths;

  aktifSekme: string = "ai";

  // Sihirbaz artık dialog değil, "Rapor Sihirbazı" sekmesinin içeriği.
  // Tasarım şablonundaki akış bu: yeni/düzenle/AI taslağı hep aynı yerde açılır.
  sihirbazAcik = false;
  sihirbazKayit: any = null;
  sihirbazGuncelle = false;
  sihirbazAdim = 1;
  sihirbazAnahtar = 0;

  sonRaporlar: Array<SonRapor> = [];

  // Excel Çıktıları sekmesinin rozet sayısı; bileşen mount olunca güncellenir
  // ve sekme kapansa bile burada (parent'ta) kalır. null → rozet gösterilmez.
  hazirExcelSayisi: number | null = null;

  headers = [
    {text: "Rapor Adı", align: "start", sortable: true, value: "isim"},
    {text: "Kolon", sortable: false, value: "kolonSayisi", width: 90},
    {text: "Kayıt", sortable: false, value: "kayitSayisi", width: 90},
    {text: "Tarih", sortable: true, value: "created_at", width: 130},
    {text: "", value: "actions", width: 140, align: "end", sortable: false}
  ];

  get sekmeler(): Array<RaporSekme> {
    return [
      {kod: "ai", etiket: "AI Sihirbazı", ikon: "mdi-creation"},
      {kod: "sihirbaz", etiket: "Rapor Sihirbazı", ikon: "mdi-tune-variant"},
      {kod: "kayitli", etiket: "Kayıtlı Raporlar", ikon: "mdi-content-save-outline", sayi: this.localValue ? this.localValue.length : 0},
      // Sayaç yalnız dolu olduğunda görünür; "0" rozeti bilgi taşımıyor, gürültü yapıyor.
      // Excel Çıktıları aynı kuralı kullanıyor: hazır dosya yokken de "0" yazılmasın
      // (RaporSekmeler yalnız null/undefined'ı gizliyor, 0'ı gizlemiyor).
      {kod: "cikti", etiket: "Rapor Çıktısı", ikon: "mdi-table", sayi: this.sonRaporlar.length || null},
      {kod: "excel", etiket: "Excel Çıktıları", ikon: "mdi-file-excel-outline", sayi: this.hazirExcelSayisi || null},
    ];
  }

  // DEV-3011 — liste ucu standart_mi döndürmüyor; kaynak alanından türetilir.
  // (Eski v-if="!item.standart_mi" her zaman doğruydu ve standart raporlarda da
  // düzenle/sil gösteriyordu.)
  isStandart(item: any): boolean {
    return item.standart_mi ?? (item.kaynak === "central");
  }

  kolonSayisi(item: any): string {
    if (item.alanlar && Array.isArray(item.alanlar)) return String(item.alanlar.length);
    return "-";
  }

  oncekiSayfa() {
    this.$router.push(this.programPaths.ofispro)
  }

  @Watch("searchText")
  onSearchTextChange() {
    this.load()
  }

  // DEV-3350: zil bildirimi ?sekme=excel ile gelir. Sorgu seçimden sonra silinir;
  // yoksa aynı adrese ikinci tık "yinelenen gezinme" sayılıp sekmeyi seçmezdi.
  @Watch("$route.query.sekme")
  onSekmeSorgusu() {
    const {sekme, ...kalan} = this.$route.query;
    if (sekme === undefined) return;
    if (sekme === "excel") this.aktifSekme = "excel";
    this.$router.replace({path: this.$route.path, query: kalan});
  }

  mounted() {
    if (!this.value) {
      this.localValue = []
    }
    this.sonRaporlar = this.sonRaporlariOku();
    this.load();
    this.onSekmeSorgusu();
  }

  load() {
    let action = "/api/v1/rapor"
    if (this.searchText && this.searchText != "") {
      action = "/api/v1/rapor/filter?isim=" + this.searchText;
    }
    this.$http.get(action).then(response => {
      this.localValue = response.data;
      // Silinen rapor listede kalmasın, adı değişen güncel adıyla görünsün
      const mevcut: any = {};
      (this.localValue || []).forEach((r: any) => { mevcut[r.id] = r.isim; });
      this.sonRaporlar = this.sonRaporlar
          .filter((s) => mevcut[s.id] !== undefined)
          .map((s) => ({...s, isim: mevcut[s.id]}));
    });
  }

  /**
   * Sihirbazı açar. kayit=null → yeni rapor. adim=4 → AI taslağı doğrudan
   * özet adımında açılır (şablondaki davranış: kullanıcı son hâli görür, kaydeder).
   * sihirbazAnahtar, RaporForm'un created() kancasının her açılışta yeniden
   * koşması için :key olarak kullanılır.
   */
  sihirbazAc(kayit: any, guncelle = false, adim = 1) {
    this.sihirbazKayit = kayit ? JSON.parse(JSON.stringify(kayit)) : new RaporEntity();
    this.sihirbazGuncelle = guncelle;
    this.sihirbazAdim = adim;
    this.sihirbazAnahtar++;
    this.sihirbazAcik = true;
    this.aktifSekme = "sihirbaz";
  }

  sihirbazKapat() {
    this.sihirbazAcik = false;
    this.sihirbazKayit = null;
    this.sihirbazGuncelle = false;
    this.sihirbazAdim = 1;
  }

  sihirbazKaydedildi() {
    this.sihirbazKapat();
    this.load();
    this.aktifSekme = "kayitli";
  }

  // AI taslağını sihirbazın beklediği biçimde forma verir (onay adımı = sihirbazın kendisi).
  // 'alanlar' bilgi listesi bilerek verilmez; RaporForm'un alanlar watch'ı
  // (düzenleme modu dönüştürücüsü) yanlış tetiklenmesin.
  aiTaslagiAc(taslak: any) {
    this.sihirbazAc({
      isim: taslak.isim,
      rapor_alanlari: taslak.rapor_alanlari,
      rapor_secilen_kosullar: taslak.rapor_secilen_kosullar,
    }, false, 4);
  }

  detay(id: number) {
    this.sonRaporaEkle(id);
    this.$router.push(ProgramPaths.ofispro + '/rapor/' + id);
  }

  /**
   * "Rapor Çıktısı" sekmesi, açılan raporların kısa listesini gösterir.
   * Liste bu tarayıcıda durur (sunucuya gitmez); rapor silinirse ya da adı
   * değişirse liste okunurken güncel kayıtla eşleştirilir.
   */
  sonRaporaEkle(id: number) {
    const kayit = (this.localValue || []).find((r: any) => r.id === id);
    if (!kayit) return;
    const yeni: SonRapor = {id, isim: (kayit as any).isim, zaman: Date.now()};
    const liste = this.sonRaporlariOku().filter((s) => s.id !== id);
    liste.unshift(yeni);
    this.sonRaporlar = liste.slice(0, SON_RAPOR_SINIRI);
    try {
      localStorage.setItem(SON_RAPOR_ANAHTARI, JSON.stringify(this.sonRaporlar));
    } catch (e) { /* localStorage kapalıysa gezinme yine de çalışsın */ }
  }

  sonRaporlariOku(): Array<SonRapor> {
    try {
      const ham = localStorage.getItem(SON_RAPOR_ANAHTARI);
      const liste = ham ? JSON.parse(ham) : [];
      return Array.isArray(liste) ? liste : [];
    } catch (e) {
      return [];
    }
  }

  sonRaporlariTemizle() {
    this.sonRaporlar = [];
    try {
      localStorage.removeItem(SON_RAPOR_ANAHTARI);
    } catch (e) { /* yok sayılır */ }
  }

  zamanEtiketi(zaman: number): string {
    const fark = Date.now() - zaman;
    const dk = Math.floor(fark / 60000);
    if (dk < 1) return "az önce";
    if (dk < 60) return dk + " dk önce";
    const saat = Math.floor(dk / 60);
    if (saat < 24) return saat + " saat önce";
    const gun = Math.floor(saat / 24);
    return gun + " gün önce";
  }
}
